import type { Camera } from '../engine/camera'
import type { GameWorld } from '../game/world'
import type { Entity } from '../game/components'
import { motherPosition } from '../game/systems/npc'
import { getActiveDungeon } from '../game/dungeon/active'
import { routeWaypoint } from '../game/dungeon/pathfind'

/** Where the arrow should AIM for a target at (tx,ty): the next waypoint
 *  along the hall graph in the walled world (a straight arrow lies through
 *  banks), the target itself in open water / same room / sealed pocket. */
function aimPoint(player: Entity, tx: number, ty: number): { x: number; y: number } {
  const layout = getActiveDungeon()
  if (layout?.nodeDepth) {
    const wp = routeWaypoint(layout, player.position.x, player.position.y, tx, ty)
    if (wp) return wp
  }
  return { x: tx, y: ty }
}

const SCREEN_MARGIN = 24 // px inside viewport edge where beacon arrow sits

interface BeaconEl {
  container: HTMLDivElement
  arrow: HTMLDivElement
  label: HTMLSpanElement
  orbRef: Entity | null
}

export interface OrbBeacon {
  tick(world: GameWorld, player: Entity, camera: Camera, canvasW: number, canvasH: number, homeAffordable?: boolean): void
  /** Flash the home arrow briefly (e.g. pressing C too far from mother). */
  pingHome(): void
}

function createBeaconEl(container: HTMLElement): BeaconEl {
  const div = document.createElement('div')
  div.className = 'orb-beacon'
  div.style.display = 'none'

  const arrow = document.createElement('div')
  arrow.className = 'orb-beacon-arrow'

  const label = document.createElement('span')
  label.className = 'orb-beacon-label'

  div.appendChild(arrow)
  div.appendChild(label)
  container.appendChild(div)

  return { container: div, arrow, label, orbRef: null }
}

function clampToEdge(
  sx: number,
  sy: number,
  canvasW: number,
  canvasH: number,
  margin: number,
): { x: number; y: number; angle: number } {
  const cx = canvasW / 2
  const cy = canvasH / 2
  const dx = sx - cx
  const dy = sy - cy
  const angle = Math.atan2(dy, dx) // angle from center toward orb

  // Clamp to viewport minus margin
  const left = margin
  const right = canvasW - margin
  const top = margin
  const bottom = canvasH - margin

  // Cast ray from center toward target, find where it hits the border
  let t = Infinity
  if (dx > 0) t = Math.min(t, (right - cx) / dx)
  else if (dx < 0) t = Math.min(t, (left - cx) / dx)
  if (dy > 0) t = Math.min(t, (bottom - cy) / dy)
  else if (dy < 0) t = Math.min(t, (top - cy) / dy)

  return {
    x: cx + dx * t,
    y: cy + dy * t,
    angle,
  }
}

export function createOrbBeacon(): OrbBeacon {
  const containerEl = document.getElementById('orb-beacons') as HTMLDivElement
  const pool: BeaconEl[] = []
  // Home beacon (monorail-3oj): always points at the mother while she's
  // off-screen — subtle baseline so the world always has a "you are here
  // relative to home" reading. It brightens (urgent) when there's a reason
  // to go: something affordable, low health, or a C-press ping from afar.
  const home = createBeaconEl(containerEl)
  home.container.classList.add('orb-beacon-home')
  home.label.textContent = 'home'
  let homePingUntil = 0

  function acquireBeacon(): BeaconEl {
    const free = pool.find(b => b.orbRef === null)
    if (free) return free
    const b = createBeaconEl(containerEl)
    pool.push(b)
    return b
  }

  return {
    pingHome() {
      homePingUntil = performance.now() + 2500
    },
    tick(world, player, camera, canvasW, canvasH, homeAffordable = false) {
      // Home arrow — anchored to the mother (she stays home, but her actual
      // spot is MOTHER_HOME, not the origin).
      const mom = motherPosition(world)
      const homeScreen = camera.worldToScreen(mom?.x ?? 0, mom?.y ?? 0)
      const aspectH = canvasW / canvasH
      const hx = homeScreen.x / canvasW - 0.5
      const hy = homeScreen.y / canvasH - 0.5
      const homeOnScreen = Math.sqrt((hx * aspectH) ** 2 + hy ** 2) * 2 < 0.82
      if (!homeOnScreen) {
        // Same low-health line the lowHealth event uses (damage.ts).
        const lowHealth = (player.health?.current ?? Infinity) <= 1
        const urgent = homeAffordable || lowHealth || performance.now() < homePingUntil
        // Aim along the web, not through the banks (B5).
        const homeAim = aimPoint(player, mom?.x ?? 0, mom?.y ?? 0)
        const homeAimScreen = camera.worldToScreen(homeAim.x, homeAim.y)
        const { x, y, angle } = clampToEdge(homeAimScreen.x, homeAimScreen.y, canvasW, canvasH, SCREEN_MARGIN)
        home.container.style.display = 'flex'
        home.container.classList.toggle('urgent', urgent)
        home.container.style.left = `${x - 5}px`
        home.container.style.top = `${y - 9}px`
        home.arrow.style.transform = `rotate(${angle + Math.PI / 2}rad)`
      } else {
        home.container.style.display = 'none'
      }

      // Mark all beacons as free before this frame
      for (const b of pool) b.orbRef = null

      for (const orb of world.with('dnaOrb', 'position')) {
        const screen = camera.worldToScreen(orb.position.x, orb.position.y)
        // Mirror the postprocess eyepiece check: only consider the orb visible
        // if it's inside the bright circular area (normR < 0.82 gives a margin
        // before the hard cutoff at 0.88 so it stays visible in the dimmed ring).
        const aspect = canvasW / canvasH
        const uvX = screen.x / canvasW - 0.5
        const uvY = screen.y / canvasH - 0.5
        const normR = Math.sqrt((uvX * aspect) ** 2 + uvY ** 2) * 2
        const onScreen = normR < 0.82

        if (onScreen) continue

        const beacon = acquireBeacon()
        beacon.orbRef = orb

        // Visibility used the orb's true spot; the arrow aims at the next
        // hall-graph waypoint so retrieval runs follow real water (B5).
        const aim = aimPoint(player, orb.position.x, orb.position.y)
        const aimScreen = camera.worldToScreen(aim.x, aim.y)
        const { x, y, angle } = clampToEdge(aimScreen.x, aimScreen.y, canvasW, canvasH, SCREEN_MARGIN)
        beacon.label.textContent = String(orb.dnaOrb.amount)
        beacon.container.style.display = 'flex'
        // Position so the center of the beacon sits at (x, y)
        beacon.container.style.left = `${x - 5}px`  // arrow is 10px wide
        beacon.container.style.top = `${y - 9}px`   // arrow is 9px tall

        // Rotate arrow to point toward orb (border-bottom triangle defaults to pointing up)
        beacon.arrow.style.transform = `rotate(${angle + Math.PI / 2}rad)`
      }

      // Hide unused beacons
      for (const b of pool) {
        if (b.orbRef === null) {
          b.container.style.display = 'none'
        }
      }
    },
  }
}
